import { useStyle } from "./style";
import { useCallback, useEffect, useMemo, useState } from "react";
import { NoInteractionPanel } from "../noInteraction/NoInteractionPanel";
import { CallInteractionPanel } from "@/components/organisms/callInteraction";
import {
	CurrentInteractionState,
	useCurrentInteractionStore,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import { InteractionType } from "@/common/types";
import { MessagingInteractionPanel } from "../messagingInteraction";
import MessageOutlet from "@/common/messages/outlet";
import { WrapUpState, useWrapUpStore } from "@/common/stores/nonpersistingStores/wrapup";
import { Flex } from "@/components/atoms/flex";
import { VoicemailInteractionPanel } from "../voicemailInteraction";
import { ArrowMaximizeFilled, ChevronLeftFilled } from "@fluentui/react-icons";
import { Button, mergeClasses, tokens } from "@fluentui/react-components";
import { AppCacheActions, CacheKey, useAppCacheStore } from "@/common/stores/persistingStores/app";
import {
	ContainerSizeDefaults,
	WindowWidthThresholds,
} from "@/common/constants";
import { useIntl } from "react-intl";
import {
	NavigationActions,
	useNavigationStore,
} from "@/common/stores/persistingStores/navigation";
import { useInteractions } from "../interaction/useInteractions";
import { useResponsiveBreakpoints } from "@/hooks/responsive";
import { EmailPreviewInteractionPanel } from "../emailPreview/EmailPreviewInteractionPanel";
import { useEmailInteractionPreview } from "../interaction/useEmailInteractionPreview";

export const WorkspacePanel = () => {
	const style = useStyle();
	const intl = useIntl();
	const { isMobile } = useResponsiveBreakpoints();
	const [selectedId, setSelectedId] = useState<string>();

	if (isMobile) {
		useInteractions(selectedId, setSelectedId);
	}
	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state
	);

	const setIsMobileDetailViewMode = useNavigationStore(
		(actions: NavigationActions) => actions.setIsMobileDetailViewMode
	);

	const wrapUp = useWrapUpStore((state: WrapUpState) => state.wrapUp);

	const appStoreActions = useAppCacheStore((state: AppCacheActions) => state);

	const { hideEmailPreview } = useEmailInteractionPreview();

	useEffect(() => {
		if (!currentInteraction.active && !currentInteraction.preview) {
			setIsMobileDetailViewMode(false);
		}
	}, [currentInteraction]);

	const resetToDefaultSize = () => {
		appStoreActions.setCache(
			CacheKey.InteractionPanelWidth,
			ContainerSizeDefaults.InteractionPanelWidth
		);
		appStoreActions.setCache(
			CacheKey.ToggledViewPanelWidth,
			window.innerWidth > WindowWidthThresholds.Large
				? ContainerSizeDefaults.ToggledViewPanelDefaultWidth
				: ContainerSizeDefaults.ToggledViewPanelMinWidth
		);
	};

	const component = useMemo(() => {
		let component: JSX.Element | undefined = undefined;
		if (currentInteraction.preview) {
			return (
				<EmailPreviewInteractionPanel
					sessionId={currentInteraction.preview.id}
					type={InteractionType.Email}
					entryPointEmail={currentInteraction.preview.entryPointEmail}
				/>
			);
		}

		const active = currentInteraction.active;
		const structure = {
			active: {
				[InteractionType.Call]: <CallInteractionPanel />,
				[InteractionType.Generic]: active?.id ? (
					<MessagingInteractionPanel
						sessionId={active.id}
						type={InteractionType.Generic}
					/>
				) : undefined,
				[InteractionType.Email]: active?.id ? (
					<MessagingInteractionPanel
						sessionId={active.id}
						type={InteractionType.Email}
						entryPointEmail={active.entryPointEmail}
					/>
				) : undefined,
				[InteractionType.Voicemail]: <VoicemailInteractionPanel />,
				[InteractionType.WhatsApp]: active?.id ? (
					<MessagingInteractionPanel
						sessionId={active.id}
						type={InteractionType.WhatsApp}
					/>
				) : undefined,
				[InteractionType.Webchat]: active?.id ? (
					<MessagingInteractionPanel
						sessionId={active.id}
						type={InteractionType.Webchat}
					/>
				) : undefined,
			},
		};

		if (currentInteraction.active?.type !== undefined) {
			component = structure.active[currentInteraction.active.type];
		}

		return component || <NoInteractionPanel />;
	}, [currentInteraction, wrapUp]);

	const resetToDefaultButton = useCallback(() => {
		return (
			!isMobile && (
				<Button
					className={mergeClasses(
						style.fixedButton,
						style.resetToDefaultPosition
					)}
					icon={
						<ArrowMaximizeFilled
							fontSize={14}
							color={tokens.colorNeutralForeground3}
						/>
					}
					size="small"
					title={intl.formatMessage({
						id: "Common.ResetToDefaultViewTitle",
					})}
					onClick={resetToDefaultSize}
				/>
			)
		);
	}, [isMobile]);

	const backToListButton = useCallback(() => {
		return (
			isMobile && (
				<Button
					className={mergeClasses(
						style.fixedButton,
						style.backToListButtonPosition
					)}
					icon={
						<ChevronLeftFilled
							fontSize={14}
							color={tokens.colorNeutralForeground3}
						/>
					}
					size="small"
					title={intl.formatMessage({
						id: "Common.BackToList",
					})}
					onClick={() => {
						setIsMobileDetailViewMode(false);
						hideEmailPreview();
					}}
				/>
			)
		);
	}, [isMobile]);

	const getStyle = () => {
		const active = currentInteraction?.active;
		const isChatType =
			active?.type === InteractionType.WhatsApp ||
			active?.type === InteractionType.Webchat;
		// The white chat background belongs to this interaction only while its conversation
		// is actually shown, so mirror the render condition used for the chat content in
		// MessagingInteractionPanel. Gating on `!wrapUp` (ANY wrap-up) instead greyed the
		// active webchat/whatsapp out whenever the agent was in wrap-up for a DIFFERENT
		// interaction (e.g. a call) or had a call in progress, even though this chat was
		// still displayed with a white message area.
		const isThisInteractionInWrapUp = !!wrapUp && wrapUp.mediaId === active?.id;
		const showWhiteChatBackground =
			isChatType &&
			!isThisInteractionInWrapUp &&
			!active?.wrapUpStopped &&
			!currentInteraction?.preview;

		return showWhiteChatBackground
			? { backgroundColor: tokens.colorNeutralBackground1 }
			: {};
	};

	return (
		<Flex column className={style.container} style={getStyle()}>
			<MessageOutlet namespace="workspace" style={{ margin: "0px 16px" }} />
			<Flex
				column
				// Key on the active interaction so switching interactions remounts this
				// container. The interaction panels render the CRM case picker; an interaction
				// switch (notably resuming a parked call from another interaction) can leave the
				// previous panel's picker DOM stranded here, and remounting the container removes
				// any such orphaned nodes so the boxes can't stack up (CC4ALL-9546).
				key={
					currentInteraction.active?.id ??
					currentInteraction.preview?.id ??
					"no-interaction"
				}
				className={style.interactionBarContainer}
			>
				{backToListButton()}
				{resetToDefaultButton()}
				{component}
			</Flex>
		</Flex>
	);
};
